/**
 * TNRS-179 — <cart-auto-update>
 *
 * Makes the cart page reflect quantity changes without the customer having to
 * press "Update cart". It wraps the cart template, listens for changes to the
 * quantity steppers, pushes them to the Cart AJAX API, and swaps in the section
 * HTML that Shopify renders back — so every server-calculated value (line
 * totals, discounts, subtotal, the free shipping bar from TNRS-175) stays
 * correct without being recomputed in JavaScript.
 *
 * The "Update cart" button is deliberately left in place: it is the fallback
 * when JavaScript is unavailable, and it still submits the form as before.
 */

const LOAD_KEY = 'component-cart-auto-update';

// Fired by ThemeBase#updateCartDrawer in theme.min.js; the header cart count
// listens for it, so we emit it too rather than reaching into the header.
const CART_UPDATE_EVENT = 'wetheme-cart-update';

// Long enough to swallow a run of clicks on "+" or a two-digit typed quantity,
// short enough that a single change still feels immediate.
const DEBOUNCE_MS = 500;

/**
 * Nodes that the section re-render would replace with an equivalent-but-empty
 * copy, destroying live state in the process: app-injected widgets (Route
 * shipping protection, dynamic checkout buttons) and text the customer may be
 * part-way through typing. The existing nodes are carried across instead.
 * Quantity steppers get the same treatment, but are matched per line further
 * down since lines can disappear.
 */
const PRESERVED_SELECTORS = [
  '.route-div',
  '.additional-checkout-buttons',
  '#CartSpecialInstructions',
  '#CartGiftNote',
];

class CartAutoUpdate extends HTMLElement {
  constructor() {
    super();

    /** @type {Map<string, number>} line item key -> requested quantity */
    this.pendingUpdates = new Map();
    this.debounceTimer = null;
    this.isUpdating = false;

    this.onClick = (event) => {
      // The theme's own Cart section handler has already adjusted the input by
      // the time this bubbles up, so we can just read the result.
      if (!event.target.closest('.js-qty__adjust')) return;
      this.queueChange(event.target.closest('.js-qty-wrapper'));
    };

    this.onQuantityChange = (event) => {
      if (!event.target.classList.contains('js-qty__num')) return;
      this.queueChange(event.target.closest('.js-qty-wrapper'), event.type === 'change');
    };
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({ key: LOAD_KEY });

    this.addEventListener('click', this.onClick);
    this.addEventListener('input', this.onQuantityChange);
    this.addEventListener('change', this.onQuantityChange);
  }

  disconnectedCallback() {
    clearTimeout(this.debounceTimer);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('input', this.onQuantityChange);
    this.removeEventListener('change', this.onQuantityChange);
  }

  /**
   * @param {HTMLElement|null} wrapper the .js-qty-wrapper whose input changed
   * @param {boolean} isCommitted true once the field has been committed (blur
   *   or Enter), when an unusable value should be reverted rather than awaited
   */
  queueChange(wrapper, isCommitted = false) {
    const input = wrapper && wrapper.querySelector('.js-qty__num');
    const lineKey = wrapper && wrapper.dataset.lineKey;
    if (!input || !lineKey) return;

    const quantity = parseInt(input.value, 10);

    // Half-typed or nonsense values: wait for the next keystroke, and put the
    // last known quantity back if the customer has finished with the field.
    if (!Number.isInteger(quantity) || quantity < 0) {
      if (isCommitted) input.value = wrapper.dataset.quantity;
      return;
    }

    if (quantity === Number(wrapper.dataset.quantity)) {
      this.pendingUpdates.delete(lineKey);
      return;
    }

    this.pendingUpdates.set(lineKey, quantity);

    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.flush(), DEBOUNCE_MS);
  }

  async flush() {
    if (this.isUpdating || !this.pendingUpdates.size) return;

    const updates = Array.from(this.pendingUpdates);
    this.pendingUpdates.clear();
    this.isUpdating = true;
    this.setAttribute('aria-busy', 'true');

    try {
      let cart = null;

      for (let index = 0; index < updates.length; index += 1) {
        const [lineKey, quantity] = updates[index];
        // Only the final change needs to pay for the re-rendered section.
        // eslint-disable-next-line no-await-in-loop
        cart = await this.changeLine(lineKey, quantity, index === updates.length - 1);
      }

      document.documentElement.dispatchEvent(
        new CustomEvent(CART_UPDATE_EVENT, { detail: { cart } })
      );

      // If more changes arrived while the request was in flight, painting now
      // would only show a state we are about to leave — let the follow-up
      // flush render the final one.
      if (!this.pendingUpdates.size) {
        this.renderSection(cart.sections && cart.sections[this.sectionId]);
      }
    } catch (error) {
      // Covers a dropped connection as much as Shopify refusing a quantity
      // that is no longer in stock. Either way the page may now be describing
      // a cart that does not exist, so go and read the real one.
      console.error('Cart auto-update failed:', error);
      await this.recover();
    } finally {
      this.isUpdating = false;
      this.removeAttribute('aria-busy');
    }

    if (this.pendingUpdates.size) this.flush();
  }

  /** Re-read the cart section from the server after a failed change. */
  async recover() {
    try {
      const url = `${this.cartUrl}?section_id=${encodeURIComponent(this.sectionId)}`;
      const response = await fetch(url, { credentials: 'same-origin' });

      if (!response.ok) {
        throw new Error(`${url} responded with ${response.status}`);
      }

      this.renderSection(await response.text());
    } catch (error) {
      console.error('Could not re-read the cart:', error);
      this.revertQuantities();
    }
  }

  /**
   * @param {string} lineKey line item key, stable across line reordering
   * @param {number} quantity
   * @param {boolean} withSection whether to ask for re-rendered section HTML
   * @returns {Promise<object>} the updated cart
   */
  async changeLine(lineKey, quantity, withSection) {
    const body = { id: lineKey, quantity };

    if (withSection) {
      body.sections = this.sectionId;
      body.sections_url = this.cartUrl;
    }

    const response = await fetch(this.cartChangeUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`${this.cartChangeUrl} responded with ${response.status}`);
    }

    return response.json();
  }

  /** @param {string|undefined} sectionHtml server-rendered cart section */
  renderSection(sectionHtml) {
    if (!sectionHtml) {
      window.location.reload();
      return;
    }

    const holder = document.createElement('div');
    holder.innerHTML = sectionHtml;

    // Not connected, so this copy is never upgraded into a second live
    // component — it is only a source of markup.
    const next = holder.querySelector('cart-auto-update');
    if (!next) {
      window.location.reload();
      return;
    }

    const focusState = this.captureFocus();
    this.carryOverLiveNodes(next);
    this.replaceChildren(...next.childNodes);
    this.restoreFocus(focusState);
  }

  /**
   * Move nodes that must survive the swap out of the live DOM and into the
   * incoming markup, in place of their freshly rendered counterparts.
   *
   * @param {HTMLElement} next the incoming <cart-auto-update>
   */
  carryOverLiveNodes(next) {
    next.querySelectorAll('.js-qty-wrapper').forEach((incoming) => {
      const live = this.wrapperFor(incoming.dataset.lineKey);
      if (!live) return;

      const liveInput = live.querySelector('.js-qty__num');
      const incomingInput = incoming.querySelector('.js-qty__num');

      // The server value wins: it accounts for quantities clamped to available
      // stock. Anything the customer has typed since has already been queued.
      if (liveInput && incomingInput) liveInput.value = incomingInput.value;
      live.dataset.quantity = incoming.dataset.quantity;

      incoming.replaceWith(live);
    });

    PRESERVED_SELECTORS.forEach((selector) => {
      const live = this.querySelector(selector);
      const incoming = next.querySelector(selector);
      if (live && incoming) incoming.replaceWith(live);
    });
  }

  /** Put every stepper back to the quantity the cart last confirmed. */
  revertQuantities() {
    this.querySelectorAll('.js-qty-wrapper').forEach((wrapper) => {
      const input = wrapper.querySelector('.js-qty__num');
      if (input) input.value = wrapper.dataset.quantity;
    });
  }

  /**
   * The focused stepper is carried across the swap as the same node, but
   * detaching it drops focus, so record enough to hand it back.
   */
  captureFocus() {
    const active = document.activeElement;
    if (!active || !this.contains(active) || !active.classList.contains('js-qty__num')) {
      return null;
    }

    const wrapper = active.closest('.js-qty-wrapper');
    if (!wrapper) return null;

    return {
      lineKey: wrapper.dataset.lineKey,
      selectionStart: active.selectionStart,
      selectionEnd: active.selectionEnd,
    };
  }

  /** @param {{lineKey: string, selectionStart: number, selectionEnd: number}|null} focusState */
  restoreFocus(focusState) {
    if (!focusState) return;

    const wrapper = this.wrapperFor(focusState.lineKey);
    const input = wrapper && wrapper.querySelector('.js-qty__num');
    if (!input) return;

    input.focus();

    // The confirmed quantity can be shorter than what was typed.
    const end = input.value.length;
    input.setSelectionRange(
      Math.min(focusState.selectionStart, end),
      Math.min(focusState.selectionEnd, end)
    );
  }

  /** @param {string} lineKey */
  wrapperFor(lineKey) {
    if (!lineKey) return null;
    return this.querySelector(`.js-qty-wrapper[data-line-key="${CSS.escape(lineKey)}"]`);
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get cartChangeUrl() {
    return this.dataset.cartChangeUrl;
  }

  get cartUrl() {
    return this.dataset.cartUrl;
  }
}

customElements.define('cart-auto-update', CartAutoUpdate);
